(() => {
  const rootSelector = '#vue-app-image-search';
  const processedCards = new WeakSet();
  const productLookupCache = new Map();
  const pagePriceCache = new Map();
  const isCustomer = document.documentElement.dataset.customer === 'true';

  const normalizeText = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();

  const getSearchResults = (payload) => {
    return payload?.resources?.results?.products || payload?.resources?.products || [];
  };

  const getPrimaryVariant = (product) => {
    if (!product) return null;
    if (Array.isArray(product.variants) && product.variants.length > 0) {
      return product.variants.find((variant) => variant.available) || product.variants[0];
    }
    return null;
  };

  const createPlusIconMarkup = () => `
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M9 3.5V14.5M3.5 9H14.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
    </svg>
  `;

  const createProjectListButton = (product) => {
    const variant = getPrimaryVariant(product);
    const productImage = product?.featured_image?.url || product?.image || '';
    const variantId = variant?.id || product?.id || '';
    const productPrice = variant?.price || product?.price || '0';

    const wrapper = document.createElement(isCustomer ? 'project-list-card-button' : 'guest-wishlist-card-button');
    wrapper.className = 'ryzo-wishlist-button ryzo-project-list-button';
    wrapper.setAttribute('data-product-id', String(product?.id || ''));
    wrapper.setAttribute('data-variant-id', String(variantId));
    wrapper.setAttribute('data-product-title', product?.title || 'Product');
    wrapper.setAttribute('data-product-price', String(productPrice));
    wrapper.setAttribute('data-product-image', productImage);
    wrapper.setAttribute('data-product-handle', product?.handle || '');
    wrapper.classList.add('card__project-list-overlay');

    const button = document.createElement('button');
    button.className = 'card__project-list-tag';
    button.type = 'button';
    button.setAttribute('aria-label', `Add ${product?.title || 'product'} to project list`);
    button.setAttribute('title', 'Add to Project List');

    if (isCustomer) {
      button.setAttribute('data-ssfy-wishlist-collection', '');
    }

    button.innerHTML = createPlusIconMarkup();
    wrapper.appendChild(button);
    return wrapper;
  };

  const extractUnitPriceFromHtml = (html) => {
    const match = html.match(/\$\s?[\d,]+(?:\.\d{2})?\s*\/\s*Sq\. Ft\./i);
    return match ? match[0].replace(/\s+/g, ' ').trim() : null;
  };

  const fetchProductPageUnitPrice = async (productUrl) => {
    if (pagePriceCache.has(productUrl)) {
      return pagePriceCache.get(productUrl);
    }

    const request = fetch(productUrl, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => (html ? extractUnitPriceFromHtml(html) : null))
      .catch(() => null);

    pagePriceCache.set(productUrl, request);
    return request;
  };

  const resolveProductFromTitle = async (titleText) => {
    const normalizedTitle = normalizeText(titleText);

    if (productLookupCache.has(normalizedTitle)) {
      return productLookupCache.get(normalizedTitle);
    }

    const request = fetch(
      `/search/suggest.json?q=${encodeURIComponent(titleText)}&resources[type]=product&resources[limit]=10&section_id=predictive-search`,
      { credentials: 'same-origin' }
    )
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        const products = getSearchResults(payload);

        if (!products.length) {
          return null;
        }

        const exactMatch = products.find((product) => normalizeText(product.title) === normalizedTitle);
        if (exactMatch) {
          return exactMatch;
        }

        const partialMatch = products.find((product) => {
          const productTitle = normalizeText(product.title);
          return productTitle.includes(normalizedTitle) || normalizedTitle.includes(productTitle);
        });

        return partialMatch || products[0] || null;
      })
      .catch(() => null);

    productLookupCache.set(normalizedTitle, request);
    return request;
  };

  const updateCardPrice = async (cardElement) => {
    if (processedCards.has(cardElement) || cardElement.dataset.ryzoEnhancing === 'true' || cardElement.dataset.ryzoEnhanced === 'true') {
      return;
    }

    cardElement.dataset.ryzoEnhancing = 'true';

    const titleElement = cardElement.querySelector('.ryzo-product-title');
    const priceElement = cardElement.querySelector('.ryzo-price');

    if (!titleElement || !priceElement) {
      cardElement.dataset.ryzoEnhancing = 'false';
      return;
    }

    try {
      priceElement.style.visibility = 'hidden';

      const wishlistButton = cardElement.querySelector('.ryzo-wishlist-button');
      if (wishlistButton) {
        wishlistButton.style.visibility = 'hidden';
        wishlistButton.style.pointerEvents = 'none';
        wishlistButton.setAttribute('aria-hidden', 'true');
      }

      const titleText = titleElement.textContent || '';
      const product = await resolveProductFromTitle(titleText);
      if (!product) {
        if (wishlistButton) {
          wishlistButton.style.visibility = 'visible';
          wishlistButton.style.pointerEvents = 'auto';
          wishlistButton.removeAttribute('aria-hidden');
        }
        priceElement.style.visibility = 'visible';
        return;
      }

      const replacementButton = createProjectListButton(product);
      replacementButton.style.visibility = 'visible';
      replacementButton.style.pointerEvents = 'auto';
      replacementButton.removeAttribute('aria-hidden');

      if (wishlistButton) {
        wishlistButton.replaceWith(replacementButton);
      }

      cardElement.classList.add('ryzo-project-list-card');
      cardElement.style.position = 'relative';

      const unitPrice = await fetchProductPageUnitPrice(product.url);
      if (unitPrice) {
        priceElement.textContent = unitPrice;
        priceElement.setAttribute('data-price-source', 'uom');
      }
      priceElement.style.visibility = 'visible';
      processedCards.add(cardElement);
    } finally {
      cardElement.dataset.ryzoEnhancing = 'false';
      cardElement.dataset.ryzoEnhanced = 'true';
    }
  };

  const scanCards = () => {
    const root = document.querySelector(rootSelector);
    if (!root) {
      return;
    }

    root.querySelectorAll('.ryzo-text-product-card').forEach((cardElement) => {
      updateCardPrice(cardElement);
    });
  };

  const observeRoot = () => {
    const root = document.querySelector(rootSelector);
    if (!root) {
      return false;
    }

    const observer = new MutationObserver(() => {
      scanCards();
    });

    observer.observe(root, { childList: true, subtree: true });
    scanCards();
    return true;
  };

  const start = () => {
    if (!observeRoot()) {
      window.setTimeout(start, 500);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();